import React, { useState } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Cancel from '@mui/icons-material/Cancel';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useTranslation } from 'react-i18next';
import { DetailPage, DetailRow, DetailSection, DetailValue } from '../../../../components/ui/detailView';
import { useNumerationSchemaNames, useTenantSettings } from '../../../../hooks/useTenantSettings';
import { KsefCertificateInfo, TenantSettings } from '../../../../types/tenantSettings';
import { formatDate, formatQuantity } from '../../../../utils';
import { SETTINGS_SECTIONS, SettingsRow, SettingsSectionKey } from '../../tenantSettings.config';

const MASKED_PLACEHOLDER = '••••••••';
const EMPTY = '—';

/** A tick or a cross - "true"/"false" reads as raw data rather than a setting. */
const BooleanValue = ({ value }: { value: boolean }) => {
  const { t } = useTranslation();
  const label = value ? t('Yes') : t('No');

  return value ? (
    <CheckCircle fontSize="small" color="success" titleAccess={label} />
  ) : (
    <Cancel fontSize="small" sx={{ color: 'text.disabled' }} titleAccess={label} />
  );
};

/** Enough about the stored certificate to tell which one is in use and when it runs out. */
const CertificateValue = ({ info }: { info: KsefCertificateInfo | null | undefined }) => {
  const { t } = useTranslation();

  if (!info) {
    return <DetailValue>{EMPTY}</DetailValue>;
  }

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="subtitle1" sx={{ wordBreak: 'break-word' }}>
        {info.subject}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {t('Valid until {{date}}', { date: formatDate(info.valid_to) })}
      </Typography>
      <Typography variant="caption" component="div" sx={{ color: 'text.secondary', wordBreak: 'break-all' }}>
        {t('SHA-256 fingerprint: {{fingerprint}}', { fingerprint: info.fingerprint })}
      </Typography>
    </Box>
  );
};

interface SettingsValueProps {
  row: SettingsRow;
  settings: TenantSettings;
  schemaNames: Record<string, string>;
}

const SettingsValue = ({ row, settings, schemaNames }: SettingsValueProps) => {
  const { t } = useTranslation();
  const [revealed, setRevealed] = useState(false);
  const value = settings[row.key];
  const isSet = value !== null && value !== undefined && value !== '';

  if (row.kind === 'boolean') {
    return <BooleanValue value={Boolean(value)} />;
  }

  // A write-only credential arrives as "is one stored?", so it is masked on its
  // own rather than through a reveal toggle that has nothing to reveal.
  if (row.kind === 'writeOnly') {
    return <DetailValue>{value ? MASKED_PLACEHOLDER : EMPTY}</DetailValue>;
  }

  if (row.kind === 'certificate') {
    return <CertificateValue info={value as KsefCertificateInfo | null | undefined} />;
  }

  if (row.kind === 'schema') {
    return <DetailValue>{(isSet && schemaNames[String(value)]) || EMPTY}</DetailValue>;
  }

  if (row.kind === 'percent') {
    return <DetailValue>{isSet ? `${formatQuantity(value)}%` : EMPTY}</DetailValue>;
  }

  const text = isSet ? String(value) : EMPTY;

  if (row.kind === 'sensitive') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        <DetailValue>{isSet && !revealed ? MASKED_PLACEHOLDER : text}</DetailValue>
        {isSet && (
          <IconButton
            size="small"
            onClick={() => setRevealed((previous) => !previous)}
            aria-label={revealed ? t('Hide') : t('Reveal')}
            sx={{ flexShrink: 0, p: 0.25 }}
          >
            {revealed ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
          </IconButton>
        )}
      </Box>
    );
  }

  if (row.kind === 'multiline') {
    return (
      <Typography variant="subtitle1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', minWidth: 0 }}>
        {text}
      </Typography>
    );
  }

  return <DetailValue>{text}</DetailValue>;
};

interface TenantSettingsSectionProps {
  section: SettingsSectionKey;
  /** Extra content rendered under the section's groups (e.g. a related list). */
  children?: React.ReactNode;
  /** Drop the intro line, for a section embedded in a page of its own. */
  hideDescription?: boolean;
}

/**
 * Renders one section of the tenant settings straight from
 * {@link SETTINGS_SECTIONS} - the same definition the section's edit form is
 * built from, so the screen and the form can never list different fields.
 */
export const TenantSettingsSection = ({
  section,
  children,
  hideDescription = false,
}: TenantSettingsSectionProps) => {
  const { t } = useTranslation();
  const { settings, loading } = useTenantSettings();
  const schemaNames = useNumerationSchemaNames();
  const { description, groups } = SETTINGS_SECTIONS[section];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // No settings means the viewer may not read them; anything the page nests
  // inside this section is theirs to see and still renders.
  return (
    <DetailPage>
      {settings && description && !hideDescription && (
        <Typography variant="body2" color="text.secondary">
          {t(description)}
        </Typography>
      )}
      {settings && groups.map((group) => {
        const rows = group.rows.filter((row) => !row.shownWhen || row.shownWhen(settings));
        if (rows.length === 0) return null;

        return (
          <DetailSection key={group.title} title={t(group.title)}>
            {rows.map((row) => (
              <DetailRow
                key={String(row.key)}
                label={t(row.label)}
                alignTop={row.kind === 'multiline' || row.kind === 'certificate'}
                value={<SettingsValue row={row} settings={settings} schemaNames={schemaNames} />}
              />
            ))}
          </DetailSection>
        );
      })}
      {children}
    </DetailPage>
  );
};
